import { MagnifyingGlass } from '@phosphor-icons/react';
import { CATEGORIES, CATEGORY_LABELS, NODE_TYPES } from '@reelplicate/flows';
import { LibraryNode, Stack, TextInput } from '@reelplicate/ui';
import { useReactFlow } from '@xyflow/react';
import { useState } from 'react';

import { nodeIcon } from '../catalogue-ui';
import { useEditorStore } from './editor-store';
import { NODE_DRAG_TYPE } from './flow-canvas';

/**
 * The left-hand node library (Figma editor, Node library): search, then every
 * node by category. Drag one onto the canvas, or click to drop it in the middle
 * of the view. Coming-soon nodes are listed but can't be added.
 */
export function LibraryPanel() {
  const store = useEditorStore();
  const { screenToFlowPosition } = useReactFlow();
  const [query, setQuery] = useState('');
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const matches = NODE_TYPES.filter((node) => {
    const text =
      `${node.label} ${node.description} ${CATEGORY_LABELS[node.category]}`.toLowerCase();
    return words.every((word) => text.includes(word));
  });

  const addInView = (type: string) => {
    const canvas = document
      .querySelector('[data-slot=flow-canvas]')
      ?.getBoundingClientRect();
    const centre = canvas
      ? screenToFlowPosition({
          x: canvas.left + canvas.width / 2,
          y: canvas.top + canvas.height / 2,
        })
      : { x: 0, y: 0 };
    // Nudge each click so repeated adds don't stack exactly.
    const offset = (store.getState().graph.nodes.length % 5) * 24;
    store
      .getState()
      .addNode(type, { x: centre.x - 124 + offset, y: centre.y - 40 + offset });
  };

  return (
    <aside
      aria-label="Node library"
      className="bg-bg-surface border-border-subtle flex w-72 shrink-0 flex-col border-r"
    >
      <div className="p-3 pb-2">
        <TextInput
          size="md"
          leadingIcon={MagnifyingGlass}
          placeholder="Search nodes"
          aria-label="Search nodes"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>
      <div className="flex-1 overflow-y-auto px-3 pb-4">
        {matches.length === 0 && (
          <p className="text-body-sm text-text-secondary px-2.5 py-4">
            No nodes match “{query}”.
          </p>
        )}
        <Stack gap={4}>
          {CATEGORIES.map((category) => {
            const nodes = matches.filter((node) => node.category === category);
            if (nodes.length === 0) return null;
            return (
              <section key={category} aria-label={CATEGORY_LABELS[category]}>
                <h3 className="text-overline text-text-tertiary px-2.5 pt-2 pb-1 uppercase">
                  {CATEGORY_LABELS[category]}
                </h3>
                {nodes.map((node) => (
                  <LibraryNode
                    key={node.type}
                    interactive
                    category={category}
                    icon={nodeIcon(node)}
                    title={node.label}
                    description={node.description}
                    soon={node.status === 'soon'}
                    draggable={node.status !== 'soon'}
                    onDragStart={(event) => {
                      event.dataTransfer.setData(NODE_DRAG_TYPE, node.type);
                      event.dataTransfer.effectAllowed = 'copy';
                    }}
                    onClick={() => addInView(node.type)}
                  />
                ))}
              </section>
            );
          })}
        </Stack>
      </div>
    </aside>
  );
}
